/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { DONE_VERTICAL } from '@/types/task';
import type { Vertical } from '@/types/session';

/**
 * A column's colour, as Tailwind classes.
 *
 * **Keyed by palette token, and every class is written out in full.** Tailwind
 * scans source text, so `bg-${token}` would compile to nothing — the classes
 * have to exist as literals somewhere, and this is that somewhere. Seven tokens,
 * four uses each, in one file rather than repeated in five components.
 *
 * The maps used to be keyed by the five column *ids*, which stopped working the
 * moment a circle could invent an id. `tokenOf` bridges the two: the five
 * originals use their own name as their token, so an un-customised board resolves
 * to exactly the classes it always had.
 */

const FALLBACK = 'action';

/** The palette token for a column, falling back for anything unrecognised. */
export function tokenOf(v: Pick<Vertical, 'id' | 'color'> | undefined): string {
  const token = v?.color || v?.id || '';
  return token in DOT ? token : FALLBACK;
}

/** The dot in a column heading: its colour, with a thin ring of the same. */
export const DOT: Record<string, string> = {
  action: 'bg-action ring-action/15',
  discovery: 'bg-discovery ring-discovery/15',
  solutioning: 'bg-solutioning ring-solutioning/15',
  wip: 'bg-wip ring-wip/15',
  complete: 'bg-complete ring-complete/15',
  teal: 'bg-teal ring-teal/15',
  rose: 'bg-rose ring-rose/15',
};

/** The count pill, in its column's colour, one step paler. */
export const COUNT: Record<string, string> = {
  action: 'bg-action/10 text-action',
  discovery: 'bg-discovery/10 text-discovery',
  solutioning: 'bg-solutioning/10 text-solutioning',
  wip: 'bg-wip/10 text-wip',
  complete: 'bg-complete/10 text-complete',
  teal: 'bg-teal/10 text-teal',
  rose: 'bg-rose/10 text-rose',
};

/** A selected chip — the modal's column row, and the filter menu. */
export const CHIP: Record<string, string> = {
  action: 'bg-action border-action',
  discovery: 'bg-discovery border-discovery',
  solutioning: 'bg-solutioning border-solutioning',
  wip: 'bg-wip border-wip',
  complete: 'bg-complete border-complete',
  teal: 'bg-teal border-teal',
  rose: 'bg-rose border-rose',
};

/**
 * **Only the done column is washed**, and it is washed wherever it sits.
 *
 * Vanilla tinted one column and nothing else, and the wash says "this work is
 * done", which is information. Tinting all five said nothing and cost the board
 * its calm. The rule now keys off the *done column's id* rather than the literal
 * 'complete' being fifth, so a circle that renames it to "Shipped" keeps the
 * wash — the id never changes, only the label.
 */
export function washFor(id: string): string {
  return id === DONE_VERTICAL ? 'bg-complete-col' : '';
}

/**
 * The small column chip in the list, archived and My Day tables.
 *
 * `v-tag v-<token>` — the vanilla handle, and the seven `.v-*` rules in
 * index.css are hand-written for the same reason the maps above are. This used
 * to interpolate the column *id*, which produced a class nothing styles the
 * moment a circle invented one: the chip did not break, it just silently lost
 * its colour.
 */
export function vTagClass(columns: Vertical[], id: string): string {
  return `v-tag v-${tokenOf(columns.find((v) => v.id === id))}`;
}

/** What to call a column, with the raw id as the last resort. */
export function labelOf(columns: Vertical[], id: string): string {
  return columns.find((v) => v.id === id)?.label ?? id;
}
